import React, { useState, useEffect } from "react";
import Footer from "./Footer";
import { CircleX } from "lucide-react";
import SafeHTML from "../../../components/SafeHTML";
import * as LucideIcons from "lucide-react";
import { useMyPRService } from "../useMyPRService";
import useStickyButton from "../hooks/useStickyButton";
import useSignedUrl from "../../../hooks/useSignedUrl";

const MyPRReject = ({ validationID, myPRSide, onRetry, uiConfig }) => {
  const { getMyPRRejectReason } = useMyPRService();
  const [title, setTitle] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessageKey, setErrorMessageKey] = useState(null);
  const [failReasonAndSolutionList, setFailReasonAndSolutionList] = useState([]);
  const [content, setContent] = useState([]);
  const [showNameComparison, setShowNameComparison] = useState(false);
  const [showIDComparison, setShowIDComparison] = useState(false);
  const [showDocumentImage, setShowDocumentImage] = useState(false);
  const [expectedName, setExpectedName] = useState("");
  const [extractedName, setExtractedName] = useState("");
  const [expectedIDNumber, setExpectedIDNumber] = useState("");
  const [extractedIDNumberFront, setExtractedIDNumberFront] = useState("");
  const [extractedIDNumberBack, setExtractedIDNumberBack] = useState("");
  const [documentFrontImageKey, setDocumentFrontImageKey] = useState("");
  const [documentBackImageKey, setDocumentBackImageKey] = useState("");
  const documentFrontImageUrl = useSignedUrl(documentFrontImageKey);
  const documentBackImageUrl = useSignedUrl(documentBackImageKey);
  const [expectedNameLabel, setExpectedNameLabel] = useState("Expected Name");
  const [extractedNameLabel, setExtractedNameLabel] = useState("Extracted Name");
  const [expectedIDNumberLabel, setExpectedIDNumberLabel] = useState("Expected ID Number");
  const [extractedIDNumberFrontLabel, setExtractedIDNumberFrontLabel] = useState("Extracted ID Number Front");
  const [extractedIDNumberBackLabel, setExtractedIDNumberBackLabel] = useState("Extracted ID Number Back");
  const { buttonContainerRef, sentinelRef, isSticky, buttonHeight } = useStickyButton();
  
  // Safe access
  const global = uiConfig?.Global || {};
  const myPRReject = uiConfig?.MyPRReject || {};
  const cardBgColor = global?.card?.backgroundColor || "#f3f4f6"; // gray-50
  const textColor = global?.text?.fontColor || "#6b7280"; // default gray-500
  const cardOpacity = global?.card?.opacity || "1"; // default opacity
  const buttonBgColor = global?.button?.backgroundColor || "#2563eb"; // blue-600
  const buttonTextColor = global?.button?.fontColor || "#ffffff"; // white
  const pageBgColor = global?.pageContainer?.backgroundColor || "#ffffff"; // white
  const highlightTextColor = global?.highlightText?.fontColor || "#1d4ed8"; // blue-700
  const showFooter = global?.Footer?.Show !== undefined ? global.Footer.Show : true;

  const getTitleHTML = () => {
    // 1. Per-error-type title from DB (set via admin portal)
    if (title) {
      return `<div style="font-size: 1.5rem; font-weight: 500; line-height: 2rem;">${title}</div>`;
    }

    // 2. Global UI config override
    const titleConfig = myPRReject?.Title?.Text;
    if (titleConfig) {
      return titleConfig;
    }

    // 3. Hardcoded default based on myPRSide
    let sideName = "";
    if (myPRSide === "blacklist" || myPRSide === "kenalID") {
      sideName = "MyPR";
    } else if (myPRSide === "selfieWithMyPR") {
      sideName = "Selfie with MyPR";
    } else if (myPRSide === "hologramDetection") {
      sideName = "MyPR Hologram Detection";
    } else {
      sideName = `MyPR ${myPRSide}.`;
    }
    
    return `<p style="font-size: 1.5rem; font-weight: 500; line-height: 2rem; color: ${textColor};">
      Oops! We couldn't verify your <span style="color: ${highlightTextColor};">${sideName}</span>
    </p>`;
  };

  const getDescriptionHTML = () => {
    const descriptionConfig = myPRReject?.Description?.Text;
    if (descriptionConfig) {
      return descriptionConfig;
    }
    // Only show description if errorMessage exists (after API call)
    if (!errorMessage) {
      return "";
    }
    return `<p style="font-size: 1rem; margin-top: 1rem; color: ${textColor};">${errorMessage}</p>`;
  };

  const getButtonConfig = () => {
    const globalButton = global?.button || {};
    const buttonConfig = myPRReject?.TryAgainButton || {};
    
    // Build base style from global button config
    const globalStyle = globalButton?.Style || `padding: 0.75rem 1rem; border-radius: 9999px; display: flex; align-items: center; justify-content: center; width: 66.67%; margin: 0 auto; background: ${buttonBgColor}; color: ${buttonTextColor}; transition: transform 0.15s ease-in-out; border: none; cursor: pointer;`;
    
    // Parse global style
    const parsedGlobalStyle = globalStyle.split(';').reduce((acc, style) => {
      const [key, value] = style.split(':').map(s => s.trim());
      if (key && value) {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    // Parse and merge specific button style if it exists
    let finalStyle = { ...parsedGlobalStyle };
    if (buttonConfig?.Style) {
      const parsedButtonStyle = buttonConfig.Style.split(';').reduce((acc, style) => {
        const [key, value] = style.split(':').map(s => s.trim());
        if (key && value) {
          acc[key] = value;
        }
        return acc;
      }, {});
      finalStyle = { ...finalStyle, ...parsedButtonStyle };
    }
    
    // Convert back to CSS string
    const mergedStyleString = Object.entries(finalStyle)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ') + ';';
    
    return {
      text: buttonConfig?.Text || globalButton?.Text || "Try Again",
      style: mergedStyleString,
      icon: {
        show: buttonConfig?.Icon?.Show !== undefined ? buttonConfig.Icon.Show : (globalButton?.Icon?.Show !== undefined ? globalButton.Icon.Show : false),
        position: buttonConfig?.Icon?.Position || globalButton?.Icon?.Position || "end",
        name: buttonConfig?.Icon?.Name || globalButton?.Icon?.Name || "RotateCcw",
        className: buttonConfig?.Icon?.ClassName || globalButton?.Icon?.ClassName || "ml-2"
      }
    };
  };

  useEffect(() => {
    if (myPRSide) {
      getMyPRRejectReason({
        validationID,
        myPRSide,
      }).then((res) => {
        if (res.status === "success") {
          setTitle(res.data.title || null);
          setErrorMessage(res.data.errorMessage);
          setErrorMessageKey(res.data.errorMessageKey);
          setFailReasonAndSolutionList(res.data.failReasonAndSolutionList || []);
          setContent(res.data.content || []);
          
          // Set comparison data if available
          setShowNameComparison(res.data.showNameComparison || false);
          setShowIDComparison(res.data.showIDComparison || false);
          setShowDocumentImage(res.data.showDocumentImage || res.data.showMyPRImage || false);
          setExpectedName(res.data.expectedName || "");
          setExtractedName(res.data.extractedName || "");
          setExpectedIDNumber(res.data.expectedIDNumber || "");
          setExtractedIDNumberFront(res.data.extractedIDNumberFront || "");
          setExtractedIDNumberBack(res.data.extractedIDNumberBack || "");
          setDocumentFrontImageKey(res.data.documentFrontImageKey || res.data.myPRFrontImageKey || "");
          setDocumentBackImageKey(res.data.documentBackImageKey || res.data.myPRBackImageKey || "");
          
          // Set customizable labels
          setExpectedNameLabel(res.data.expectedNameLabel || "Expected Name");
          setExtractedNameLabel(res.data.extractedNameLabel || "Extracted Name");
          setExpectedIDNumberLabel(res.data.expectedIDNumberLabel || "Expected ID Number");
          setExtractedIDNumberFrontLabel(res.data.extractedIDNumberFrontLabel || "Extracted ID Number Front");
          setExtractedIDNumberBackLabel(res.data.extractedIDNumberBackLabel || "Extracted ID Number Back");
        } else {
          setErrorMessage("There was an error while verifying your MyPR.");
        }
      });
    }
  }, [myPRSide]);

  return (
    <div
      style={{
        background: pageBgColor,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        padding: "1rem",
        height: "100%",
      }}
    >
      <div
        style={{
          background: cardBgColor,
          opacity: cardOpacity,
          padding: "2rem",
          borderRadius: "1rem",
          marginTop: "calc(100vh / 16)",
          marginBottom: "2rem",
        }}
      >
        <div className="flex flex-col items-center mb-8">
          <CircleX height="72" width="72" color="red" />
        </div>
        <div className="flex flex-col">
          <SafeHTML html={getTitleHTML()} />
          <SafeHTML html={getDescriptionHTML()} />
          {errorMessageKey === "IdentityMismatch" && (
            <div>
              {/* Name Comparison */}
              {showNameComparison && expectedName && extractedName && (
                <div style={{ marginTop: "1.5rem", padding: "0.875rem", backgroundColor: "#fef3c7", borderRadius: "0.5rem", border: "1px solid #fbbf24" }}>
                  <div style={{ marginBottom: "0.625rem" }}>
                    <p style={{ fontSize: "0.65rem", fontWeight: "600", color: "#92400e", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {expectedNameLabel}
                    </p>
                    <p style={{ fontSize: "0.8rem", fontWeight: "500", color: "#1f2937", marginTop: "0.25rem" }}>
                      {expectedName}
                    </p>
                  </div>
                  <div style={{ borderTop: "1px solid #fbbf24", paddingTop: "0.625rem" }}>
                    <p style={{ fontSize: "0.65rem", fontWeight: "600", color: "#92400e", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {extractedNameLabel}
                    </p>
                    <p style={{ fontSize: "0.8rem", fontWeight: "500", color: "#1f2937", marginTop: "0.25rem" }}>
                      {extractedName}
                    </p>
                  </div>
                </div>
              )}

              {/* ID Number Comparison */}
              {showIDComparison && expectedIDNumber && (extractedIDNumberFront || extractedIDNumberBack) && (
                <div style={{ marginTop: "1rem", padding: "0.875rem", backgroundColor: "#dbeafe", borderRadius: "0.5rem", border: "1px solid #60a5fa" }}>
                  <div style={{ marginBottom: "0.625rem" }}>
                    <p style={{ fontSize: "0.65rem", fontWeight: "600", color: "#1e3a8a", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {expectedIDNumberLabel}
                    </p>
                    <p style={{ fontSize: "0.8rem", fontWeight: "500", color: "#1f2937", marginTop: "0.25rem" }}>
                      {expectedIDNumber}
                    </p>
                  </div>
                  <div style={{ borderTop: "1px solid #60a5fa", paddingTop: "0.625rem", marginBottom: "0.625rem" }}>
                    <p style={{ fontSize: "0.65rem", fontWeight: "600", color: "#1e3a8a", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {extractedIDNumberFrontLabel}
                    </p>
                    <p style={{ fontSize: "0.8rem", fontWeight: "500", color: "#1f2937", marginTop: "0.25rem" }}>
                      {extractedIDNumberFront.replace(/\-/g, "")}
                    </p>
                  </div>
                  <div style={{ borderTop: "1px solid #60a5fa", paddingTop: "0.625rem" }}>
                    <p style={{ fontSize: "0.65rem", fontWeight: "600", color: "#1e3a8a", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {extractedIDNumberBackLabel}
                    </p>
                    <p style={{ fontSize: "0.8rem", fontWeight: "500", color: "#1f2937", marginTop: "0.25rem" }}>
                      {extractedIDNumberBack.replace(/\-/g, "").slice(0, 12)}
                    </p>
                  </div>
                </div>
              )}

              {/* Document Image */}
              {showDocumentImage && documentFrontImageKey && (
                <div style={{ marginTop: "1rem", padding: "0.875rem", backgroundColor: "#f3f4f6", borderRadius: "0.5rem", border: "1px solid #d1d5db" }}>
                  <p style={{ fontSize: "0.7rem", fontWeight: "500", color: "#4b5563", marginBottom: "0.25rem" }}>
                    MyPR Front
                  </p>
                  <img
                    src={documentFrontImageUrl}
                    alt="Cropped MyPR Front"
                    style={{ width: "100%", borderRadius: "0.375rem", border: "1px solid #d1d5db" }}
                  />
                  {documentBackImageKey && (
                    <>
                      <p style={{ fontSize: "0.7rem", fontWeight: "500", color: "#4b5563", marginTop: "1rem", marginBottom: "0.25rem" }}>
                        MyPR Back
                      </p>
                      <img
                        src={documentBackImageUrl}
                        alt="Cropped MyPR Back"
                        style={{ width: "100%", borderRadius: "0.375rem", border: "1px solid #d1d5db" }}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {content.length > 0 ? (
            <div className="mt-4" style={{ color: textColor }}>
              {content.map((block, index) => (
                <div
                  key={index}
                  style={{ marginBottom: "0.5rem" }}
                  dangerouslySetInnerHTML={{ __html: block }}
                />
              ))}
            </div>
          ) : (
            <ul className="text-gray-600 text-md mt-4">
              {failReasonAndSolutionList.map((item, index) => (
                <li key={index} className="mt-2">
                  <p className="text-gray-800 text-md font-medium">{item.reason}</p>
                  <p className="text-gray-600 text-md">{item.solution}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex flex-1" />

      {/* Sentinel element for Intersection Observer */}
      <div ref={sentinelRef} style={{ height: "1px", visibility: "hidden" }} />

      {/* Spacer to maintain space when button is sticky */}
      {isSticky && buttonHeight > 0 && (
        <div style={{ height: `${buttonHeight}px`, width: "100%" }} />
      )}

      <div 
        ref={buttonContainerRef}
        style={{
          position: "sticky",
          bottom: "0",
          left: "0",
          right: "0",
          width: "100%",
          padding: "1rem",
          background: "transparent",
          zIndex: "20",
          transition: "all 0.3s ease-in-out",
          marginTop: "auto",
        }}
      >
        {(() => {
          const btnConfig = getButtonConfig();
          const IconComponent = btnConfig.icon.show && LucideIcons[btnConfig.icon.name] ? LucideIcons[btnConfig.icon.name] : null;
          
          const buttonStyle = btnConfig.style.split(';').reduce((acc, style) => {
            const [key, value] = style.split(':').map(s => s.trim());
            if (key && value) {
              const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
              acc[camelKey] = value;
            }
            return acc;
          }, {});

          return (
            <button
              style={{ ...buttonStyle, pointerEvents: 'auto' }}
              onClick={onRetry}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
              onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
              onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {btnConfig.icon.position === "start" && IconComponent && (
                <IconComponent className={btnConfig.icon.className} />
              )}
              <span>{btnConfig.text}</span>
              {btnConfig.icon.position === "end" && IconComponent && (
                <IconComponent className={btnConfig.icon.className} />
              )}
            </button>
          );
        })()}
      </div>

      {showFooter && (
        <Footer style={{ fontSize: "0.75rem", color: textColor, marginTop: "1rem" }} />
      )}
    </div>
  );
};

export default MyPRReject;
